import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { useNow } from '@hooks/useNow';
import { type Delivery, type DeliveryBoardStatus, type Supplier } from '@interfaces/ILogistics';
import { supplierById } from '@d-orca/shared/client/logistics';
import { TruckIcon } from '@utils/icons';
import { EmptyStateTile } from '@components/ui/EmptyStateTile/EmptyStateTile';
import { Panel } from '../Panel';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { applyColumnDrag, countByStatus, groupDeliveries, readDragData } from '../board.logic';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { BoardCardPreview } from './BoardCard';
import { BoardColumn } from './BoardColumn';
import { NoMatches } from './NoMatches';
import { StatusesMenu } from './StatusesMenu';
import styles from './DeliveryBoard.module.css';

export interface IDeliveryBoardProps {
  deliveries: Delivery[];
  /** Every delivery on the site, search or no search. The Manage-statuses
   *  counts read these: a count gates Delete, and a status whose cards the
   *  search merely hides is still in use. */
  allDeliveries: Delivery[];
  /** The site's column set, in board order. Empty while the query is in
   *  flight — the board then renders its frame with no columns rather than
   *  guessing a default set that the site may have renamed. */
  statuses: DeliveryBoardStatus[];
  onCreate: () => void;
  onOpenDetail: (d: Delivery) => void;
  /** A card was dropped on a different column. The parent decides what that
   *  means — a plain move is patched straight through, a drop into Delivered
   *  opens the arrival dialog instead (spec §2). */
  onMoveCard: (d: Delivery, statusId: string) => void;
  /** Stars or unstars an order. Only offered to a writer, and not while locked. */
  onToggleCritical?: (d: Delivery) => void;
  onReorderColumns: (order: string[]) => void;
  onCreateStatus: (name: string) => void;
  onRenameStatus: (statusId: string, name: string) => void;
  onDeleteStatus: (statusId: string) => void;
  /** Any status write in flight — disables the dropdown's own controls. */
  statusesBusy: boolean;
  /** Ref currently being deleted — its card fades under a "Deleting…" overlay. */
  deletingRef: string | null;
  /**
   * A delivery to jump to, raised by a stock row that names one.
   *
   * Carries a `nonce` because the interesting event is the REQUEST, not the
   * value: asking for the same delivery twice must replay the scroll and the
   * flash rather than look like no change at all. Null until something asks.
   */
  jumpTo: { ref: string; nonce: number } | null;
  /** The PROJECT's IANA timezone. Every day-level reading on this board —
   *  which day an arrival landed on, whether a card is overdue — is a question
   *  about the site's calendar, not the viewer's or the server's. */
  tz: string;
  /** The Materials segment's unified search has a query. An empty board then
   *  means "no matches", not "no deliveries yet" — the guided first-delivery
   *  CTA would be a lie, so a plain no-match line renders instead. */
  searchActive?: boolean;
  /** The caller's role may write (the page's `canEdit`). When false the
   *  create buttons, the statuses menu's write controls and the column grips
   *  are not rendered. Without it, or while the page's edit lock holds,
   *  DndContext gets no sensors, so neither a card nor a column can start a
   *  drag. Opening a card's drawer stays live. */
  canEdit: boolean;
  /** The site's suppliers — resolved to a name on each card. */
  suppliers: Supplier[];
}

/**
 * The delivery kanban: one column per board status, cards dragged between
 * them, columns dragged into a new order.
 *
 * Owns only view state — which Delivered columns are expanded, the jump
 * scroll/flash, and the drag gestures. Every write goes out through a callback
 * so the page can keep the mutations (and their optimistic cache updates) in
 * one place.
 */
export function DeliveryBoard({
  deliveries,
  allDeliveries,
  statuses,
  onCreate,
  onOpenDetail,
  onMoveCard,
  onToggleCritical,
  onReorderColumns,
  onCreateStatus,
  onRenameStatus,
  onDeleteStatus,
  statusesBusy,
  deletingRef,
  jumpTo,
  tz,
  searchActive = false,
  canEdit,
  suppliers,
}: IDeliveryBoardProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  // Ticks, so "today" rolls over at the site's midnight on a page left open —
  // an overdue badge appears without waiting for an unrelated re-render.
  const now = useNow(60_000);
  /** Delivered columns the user expanded past the 14-day window. Local on
   *  purpose: how much of a column you are looking at is a property of this
   *  board's view, not of the page's data. */
  const [showAll, setShowAll] = useState<ReadonlySet<string>>(() => new Set());
  /** Which kind of thing is mid-drag, so a column drag can be pinned to the
   *  horizontal axis while a card stays free to travel anywhere. */
  const [dragKind, setDragKind] = useState<'card' | 'column' | null>(null);
  /** The delivery under a CARD drag, feeding the `DragOverlay` ghost below —
   *  null otherwise, including during a column drag (columns render their own
   *  transform in place; only cards need to escape their column's stacking
   *  context, since only a card drag crosses INTO a different column's box). */
  const [activeCard, setActiveCard] = useState<Delivery | null>(null);
  /**
   * A card just dropped on another column, still waiting for `deliveries` to
   * reflect the move. The optimistic cache write lands a TASK later than the
   * drag-end render (react-query batches notifications on a timeout), and in
   * that gap the source card would otherwise paint one frame back at full
   * opacity in its OLD column — a successful drop that read as a rejection.
   * Until the prop changes, the card keeps its drag fade.
   *
   * Carries the `deliveries` reference it was raised against and is cleared —
   * render-phase, same pattern as the jump below — the moment the prop is a
   * DIFFERENT reference, whatever it now says: the optimistic write, an error
   * rollback, and a refetch all produce a new array, and keying on "did the
   * status match the drop" instead would leave a permanently ghosted card
   * whenever the move failed.
   */
  const [pendingMove, setPendingMove] = useState<{
    ref: string;
    deliveries: Delivery[];
  } | null>(null);
  if (pendingMove && pendingMove.deliveries !== deliveries) setPendingMove(null);

  /**
   * `pendingMove`'s column-drag sibling: the order a column was just dropped
   * into, held until the `statuses` prop reflects it. Worse than the card gap
   * when left alone — on release dnd-kit clears the sort transforms while
   * their 200ms transition is still attached, so the columns ANIMATED back
   * toward the old order and then teleported into the new one when the
   * optimistic write landed. Rendering the dropped order here, in the same
   * commit that clears the transforms, means the columns never move at all.
   *
   * Same clearing rule and for the same reason: any new `statuses` reference
   * wins, so an error rollback snaps the columns back instead of leaving the
   * board stuck on an order the server refused.
   */
  const [pendingOrder, setPendingOrder] = useState<{
    order: string[];
    statuses: DeliveryBoardStatus[];
  } | null>(null);
  if (pendingOrder && pendingOrder.statuses !== statuses) setPendingOrder(null);
  const orderedStatuses = pendingOrder
    ? [...statuses].sort(
        (a, b) => pendingOrder.order.indexOf(a.id) - pendingOrder.order.indexOf(b.id),
      )
    : statuses;

  const grouped = groupDeliveries(deliveries, statuses);
  // The headers count the UNFILTERED board (a search narrows the cards, not
  // the totals) — counted in one memoised pass, no per-render regrouping.
  const countsById = useMemo(() => countByStatus(allDeliveries, statuses), [allDeliveries, statuses]);

  /**
   * A jump lands on a card that is actually rendered.
   *
   * The Delivered column caps itself at the last 14 days, so a jump to an
   * older arrival would otherwise scroll to nothing and flash nothing. Adjusted
   * DURING render, not from an effect — the render-phase "adjust state from
   * previous renders" pattern (react.dev), the same one the list this replaces
   * used to switch its Active/Settled segment. React re-runs this component
   * immediately and commits once, so the card is already mounted when the
   * effect below looks for it.
   *
   * Keyed on the NONCE so it fires once per request; keyed on the ref instead,
   * a user who collapsed the column by hand would be overruled on every
   * unrelated re-render.
   */
  const [jumpedNonce, setJumpedNonce] = useState(0);
  if (jumpTo && jumpTo.nonce !== jumpedNonce) {
    setJumpedNonce(jumpTo.nonce);
    const target = deliveries.find((d) => d.ref === jumpTo.ref);
    if (target) {
      setShowAll((prev) =>
        prev.has(target.status) ? prev : new Set(prev).add(target.status),
      );
    }
  }

  /**
   * ...then scroll to it and flash it.
   *
   * `rows` is a ref map rather than a DOM id: refs are unique per board
   * instance, and two boards on one page must not fight over `#DEL-300`.
   * A frame of delay because rAF fires after layout, which is when the card
   * finally has a position to scroll to.
   *
   * Two axes, deliberately split. Vertical scrolls the whole PIPELINE panel into
   * view (not the card): jumping from the inventory list below used to yank the
   * target card flush to the top with the rest of the board cut off above it.
   * The panel is the board's closest `<section>`; it scrolls to the top of the
   * content area, which already sits below the fixed TopBar. Horizontal brings
   * the card's column into the board's OWN scroller, so a jump to a column that
   * is scrolled off still lands on the card without touching the page scroll.
   */
  const rows = useRef(new Map<string, HTMLElement>());
  const boardScroll = useRef<HTMLDivElement>(null);
  const [flashRef, setFlashRef] = useState<{ ref: string; nonce: number } | null>(null);
  useEffect(() => {
    if (!jumpTo) return;
    const frame = requestAnimationFrame(() => {
      const card = rows.current.get(jumpTo.ref);
      // No entry means no such card on screen — a jump to a delivery that is not
      // on the board is silently a no-op rather than a scroll to nowhere.
      if (!card) return;
      card.closest('section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Measured once, before any smooth scroll below has progressed (they are
      // async) — and the two scrollers move on independent axes, so this stays
      // the card's pre-scroll rect for both deltas.
      const cardRect = card.getBoundingClientRect();
      const scroller = boardScroll.current;
      if (scroller) {
        const scrollerRect = scroller.getBoundingClientRect();
        // Centre the card horizontally; only the board's own scrollLeft moves.
        scroller.scrollBy({
          left: cardRect.left + cardRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2),
          behavior: 'smooth',
        });
      }
      // Vertical, the third axis: each column is a fixed-height stack with its
      // OWN scroller (`.stack`, overflow-y). A card far down a long column is
      // off-screen inside that stack, and neither the section scroll nor the
      // board's horizontal scroll touches it — so a jump "down the list" landed
      // on a card nobody could see. Centre it in its own stack the same way.
      const stack = card.closest<HTMLElement>(`.${styles.stack}`);
      if (stack) {
        const stackRect = stack.getBoundingClientRect();
        stack.scrollBy({
          top: cardRect.top + cardRect.height / 2 - (stackRect.top + stackRect.height / 2),
          behavior: 'smooth',
        });
      }
      setFlashRef({ ref: jumpTo.ref, nonce: jumpTo.nonce });
    });
    return () => cancelAnimationFrame(frame);
    // `jumpTo` alone: it is the request, and it carries a fresh nonce each
    // time. Depending on `deliveries` too would re-scroll the page on every
    // background refetch, long after the user asked for anything.
  }, [jumpTo]);

  /* A small movement threshold before a pointerdown counts as a drag. Load
     bearing, not tuning: dnd-kit's default PointerSensor activates from the
     first pixel and attaches a capture-phase document `click` blocker, which
     swallows the tap that opens a card's detail drawer. The cards carry no
     click-vs-drag workaround of their own — that distinction belongs here,
     with the sensors. Same convention as DocumentsPage.
     The keyboard sensor lets a focused column grip be picked up with Space and
     moved with the arrows; cards stay pointer-only (see BoardCard's activator). */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /* Cards land wherever the pointer is (columns overlap nothing, and a card
     dragged over a tall column must not snap to a neighbour whose centre is
     closer); a column being reordered is matched against the other columns'
     centres, which is what makes the shuffle feel settled.
     Delivered (the system column) is pinned as the rightmost column: it is
     not in the sortable list (no grip, never shifted) and is no target for a
     column, so no column can be dropped onto or past it. Cards still land on
     it like any column. */
  const movableIds = orderedStatuses.filter((status) => !status.system).map((status) => status.id);
  const collisionDetection: CollisionDetection = (args) =>
    args.active.data.current?.type === 'card'
      ? pointerWithin(args)
      : closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) => movableIds.includes(String(c.id))),
        });

  function handleDragEnd(e: DragEndEvent) {
    setDragKind(null);
    setActiveCard(null);
    const { active, over } = e;
    if (!over) return;
    const dragged = readDragData(active.data.current);
    if (dragged?.type === 'column') {
      // Against the DISPLAYED order — if a previous drop is still settling,
      // the user dragged relative to what they saw, not the not-yet-updated
      // prop.
      const next = applyColumnDrag(
        orderedStatuses.map((status) => status.id),
        String(active.id),
        String(over.id),
      );
      if (next.some((id, i) => id !== orderedStatuses[i]!.id)) {
        setPendingOrder({ order: next, statuses });
        onReorderColumns(next);
      }
      return;
    }
    if (dragged?.type === 'card') {
      // Columns are the only droppables, so `over` is one either way — the
      // data lookup just avoids depending on the id being the status id.
      const dropTarget = readDragData(over.data.current);
      const targetId = dropTarget?.type === 'column' ? dropTarget.statusId : String(over.id);
      const card = deliveries.find((d) => d.ref === dragged.ref);
      if (!card || !statuses.some((status) => status.id === targetId)) return;
      if (card.status === targetId) return;
      setPendingMove({ ref: card.ref, deliveries });
      onMoveCard(card, targetId);
    }
  }

  function handleDragStart(e: DragStartEvent) {
    const dragged = readDragData(e.active.data.current);
    setDragKind(dragged?.type ?? null);
    setActiveCard(
      dragged?.type === 'card' ? (deliveries.find((d) => d.ref === dragged.ref) ?? null) : null,
    );
  }

  return (
    <Panel
      tour="logistics-pipeline"
      title={t('logistics.pipeline.title')}
      subtitle={t('logistics.pipeline.subtitle')}
      headerRight={
        <div className={styles.headerActions}>
          {canEdit && (deliveries.length > 0 || searchActive) ? (
            <button
              type="button"
              className={styles.addBtn}
              onClick={onCreate}
              disabled={locked}
              data-tour="logistics-new-delivery"
            >
              {t('logistics.pipeline.newDelivery')}
            </button>
          ) : null}
          {/* Settings sit at the header's far right edge. Stays on an empty
              board: a site may well want its columns named before the first
              delivery is written up. */}
          <StatusesMenu
            statuses={orderedStatuses}
            countsById={countsById}
            /* The lock rides the busy flag: both mean "no status write may
               start right now", and busy already disables every one of the
               menu's own controls (add, rename, delete) while leaving the
               list itself readable. */
            busy={statusesBusy || locked}
            canEdit={canEdit}
            onCreate={onCreateStatus}
            onRename={onRenameStatus}
            onDelete={onDeleteStatus}
          />
        </div>
      }
    >
      {deliveries.length === 0 && searchActive ? (
        <NoMatches />
      ) : deliveries.length === 0 ? (
        /* The shared guided-empty tile (`ui/EmptyStateTile`), with this
           panel's own CTA button inside it. */
        <EmptyStateTile
          icon={<TruckIcon size={22} />}
          title={t('logistics.pipeline.empty.title')}
          body={t('logistics.pipeline.empty.body')}
        >
          {canEdit ? (
            <button type="button" className={styles.addBtn} onClick={onCreate} disabled={locked}>
              {t('logistics.pipeline.empty.cta')}
            </button>
          ) : null}
        </EmptyStateTile>
      ) : (
        <DndContext
          /* No sensors, no drag: the one switch that stops cards AND columns
             without touching every draggable's own `disabled`. */
          sensors={canEdit && !locked ? sensors : []}
          collisionDetection={collisionDetection}
          modifiers={dragKind === 'column' ? [restrictToHorizontalAxis] : undefined}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setDragKind(null);
            setActiveCard(null);
          }}
        >
          <SortableContext
            items={movableIds}
            strategy={horizontalListSortingStrategy}
          >
            <ScrollArea orientation="horizontal" viewportRef={boardScroll}>
              {/* While a search is active the columns drop their fixed height
                  and size to the tallest column's matches, up to that same
                  height — a handful of hits does not warrant a full-height
                  board of empty columns. */}
              <div
                className={styles.board}
                data-searching={searchActive || undefined}
                data-locked={locked || undefined}
              >
                {orderedStatuses.map((status) => (
                  <BoardColumn
                    key={status.id}
                    status={status}
                    cards={grouped.get(status.id) ?? []}
                    tz={tz}
                    now={now}
                    showAll={showAll.has(status.id)}
                    onToggleShowAll={() =>
                      setShowAll((prev) => {
                        const next = new Set(prev);
                        if (!next.delete(status.id)) next.add(status.id);
                        return next;
                      })
                    }
                    onToggleCritical={canEdit && !locked ? onToggleCritical : undefined}
                    deletingRef={deletingRef}
                    dropPendingRef={pendingMove?.ref ?? null}
                    flashRef={flashRef}
                    onFlashEnd={() => setFlashRef(null)}
                    registerNode={(ref, el) => {
                      if (el) rows.current.set(ref, el);
                      else rows.current.delete(ref);
                    }}
                    onOpenDetail={onOpenDetail}
                    searchActive={searchActive}
                    canEdit={canEdit}
                    suppliers={suppliers}
                  />
                ))}
              </div>
            </ScrollArea>
          </SortableContext>
          {/* Portalled to document.body by dnd-kit — the one piece of a card
              drag that isn't a descendant of the column it started in, which
              is what keeps it painting on top of every column it passes over
              instead of sliding behind one later in the DOM.

              `dropAnimation={null}`, i.e. the ghost simply stops existing on
              release. dnd-kit's default drop animation flies the overlay to
              the rect of the ORIGINAL draggable node, measured at drop time
              (`useDropAnimation` → `active.rect`) — and at drop time that node
              is still sitting in the column the card came FROM: the move is
              optimistic, but `onMutate` only reaches `setQueryData` a
              microtask later, so the card re-renders in its new column a beat
              after the animation has already been handed its target. The
              result was a successful drop that played as a rejection — the
              ghost sailing back to the old column while the card popped into
              the new one. There is nothing to animate TO here; the card is
              already where the user put it. */}
          <DragOverlay dropAnimation={null}>
            {activeCard ? <BoardCardPreview
                delivery={activeCard}
                tz={tz}
                now={now}
                supplierName={supplierById(suppliers, activeCard.supplierId)?.name ?? null}
              /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </Panel>
  );
}
